import React from 'react';
import { FormattedMessage } from 'react-intl';
import * as mobx from 'mobx';
import * as mobxReact from 'mobx-react';
import { FormField } from '../../../../../global/form/form-field/form-field';
import { DefaultDatePicker } from '../../../../../global/default-datepicker/default-datepicker';
import { CreateZoomMeetingFormDataVM } from './zoom-meeting-form-data.viewmodel';
import { SelectTimezones } from '../../../../../global/select-timezone/select-timezones';
import { SelectGroup } from '../../../../../global/select-group/select-group';
import { ConnectionType } from '../../../../../../api/connections.dao';
import { ZoomMeetingVM } from './zoom-meeting.viewmodel';
import { OEToggle } from '../../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../../global/text/text';
import { ApprovalType } from '../_create-zoom-webinar-modal/create-zoom-webinar-form-data.viewmodel';
import { hasPermission } from '../../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../../api/roles.dao';
import { InfoPopover } from '../../../../../global/info-popover/info-popover';
import { ConnectionVersion } from '../../../../../../constants/common.constants';
import { zoomMeetingAutoRegistrationInfoMessage, zoomMeetingAutoSyncInfoMessage, zoomMeetingAutoSyncInfoMessageV2 } from '../../../../../../constants/connections-labels.constants';

interface IProps {
    isEditMode: boolean;
    formVM: ZoomMeetingVM;
    formData: CreateZoomMeetingFormDataVM;
    showAutoRegAndAutoSyncToggles: boolean;
}

const ScheduledMeetingForm = mobxReact.observer((props: IProps) => {
    const { isEditMode, formVM, formData, showAutoRegAndAutoSyncToggles } = props;

    return (
        <>
            <div className='m-bottom-25px'>
                {
                    isEditMode ? (
                        <div className='m-bottom-5px w-100'>
                            <FormattedMessage id='oe.input.meetingId' description='Input label' defaultMessage='Meeting Id' />
                            <p className='m-top-5px' style={{ fontWeight: 'bold' }}>{formVM.getCurrentZoomMeetingId()}</p>
                        </div>
                    ) : null
                }
                <div className='oe-form__row'>
                    <FormField className='m-bottom-4px' label={
                        <FormattedMessage id='oe.input.startDateTime' description='Input label' defaultMessage='Start Date and Time' />
                    }>
                        <DefaultDatePicker
                            selected={formData.getStartDateTime()}
                            onChange={date => { formData.setStartDateTime(date); }}
                            minDate={formData.getStartMinDate()}
                            maxDate={null}
                            placeholderText='Select start date time'
                            className='oe-form-field__control'
                        />
                    </FormField>

                    <FormField className='m-bottom-4px' label={
                        <FormattedMessage id='oe.input.endDateTime' description='Input label' defaultMessage='End Date and Time' />
                    }>
                        <DefaultDatePicker
                            selected={formData.getEndDateTime()}
                            onChange={date => { formData.setEndDateTime(date); }}
                            placeholderText='Select end date time'
                            minDate={formData.getStartDateTime() || new Date()}
                            maxDate={null}
                            className='oe-form-field__control'
                        />
                    </FormField>
                </div>
                {formData.getFieldError('dates') &&
                    <span className='oe-form-field__error'>{formData.getFieldError('dates')}</span>}
            </div>
            <FormField label={
                <FormattedMessage id='oe.input.timeZone' description='Input label' defaultMessage='Time Zone' />
            }>
                <SelectTimezones
                    className='oe-form-field__control'
                    value={formData.getTimeZone()}
                    onChange={mobx.action(timeZone => formData.setTimeZone(timeZone))}
                />
            </FormField>
            {
                !isEditMode ? (
                    <FormField label={
                        <FormattedMessage id='oe.input.zoomAccountGroup' description='Input label' defaultMessage='Zoom Account Group' />
                    }>
                        <SelectGroup
                            onChange={groupId => formData.setGroup(groupId)}
                            value={formData.getGroup()}
                            type={ConnectionType.Zoom}
                            className='field w-100 oe-form-field__control'
                        />
                    </FormField>
                ) : null
            }
            <FormField label={
                <FormattedMessage id='oe.input.meetingTopic' description='Input label' defaultMessage='Meeting Topic' />
            }>
                <input
                    className='oe-form-field__control'
                    value={formData.getMeetingTopic()}
                    onChange={e => { formData.setMeetingTopic(e.target.value); }}
                    onBlur={() => { formData.checkMeetingTopic(); }}
                />
                {formData.getFieldError('meetingTopic') &&
                    <span className='oe-form-field__error'>{formData.getFieldError('meetingTopic')}</span>}
            </FormField>
            {!isEditMode &&
                <>
                    {
                        showAutoRegAndAutoSyncToggles && (
                            <>
                                {formVM.getConnectionVersion() !== ConnectionVersion.V2 &&
                                    <FormField className='form-toggle'>
                                        <OEToggle
                                            isSelected={formData.autoRegistrationEnabled}
                                            onClick={formData.togglePassportAutoRegistration}
                                            dataTestId='zoom-autoRegistration-toggle'
                                        />
                                        <Text
                                            className={'m-left-8px'}
                                            message={formData.autoRegistrationEnabled ? 'Passport Auto Registration enabled' : 'Passport Auto Registration disabled'}
                                            id={formData.autoRegistrationEnabled ? 'oe.zoomWebinar.autoRegistrationEnabled' : 'oe.zoomWebinar.autoRegistrationDisabled'}
                                        />
                                        <InfoPopover className='m-left-8px'>
                                             <div>{zoomMeetingAutoRegistrationInfoMessage}</div>
                                        </InfoPopover>
                                    </FormField>
                                }
                                <FormField className='form-toggle'>
                                    <OEToggle
                                        isSelected={formData.autoSyncEnabled}
                                        onClick={formData.toggleZoomAutoSync}
                                        isDisabled={formData.autoRegistrationEnabled}
                                        dataTestId='zoom-autoSync-toggle'
                                    />
                                    <Text
                                        className={'m-left-8px'}
                                        message={formData.autoSyncEnabled ? 'Sync Meeting Attendees with Zoom enabled' : 'Sync Meeting Attendees with Zoom disabled'}
                                        id={formData.autoSyncEnabled ? 'oe.zoomMeeting.autoSyncEnabled' : 'oe.zoomMeeting.autoSyncDisabled'}
                                    />
                                    <InfoPopover className='m-left-8px'>
                                        <div>
                                            {formVM.getConnectionVersion() === ConnectionVersion.V2
                                                ? zoomMeetingAutoSyncInfoMessageV2
                                                : zoomMeetingAutoSyncInfoMessage
                                            }
                                        </div>
                                    </InfoPopover>
                                </FormField>
                            </>
                        )
                    }
                    <FormField className='form-toggle'>
                        <OEToggle
                            isSelected={formData.registrationEnabled}
                            onClick={formData.toggleRegistration}
                            isDisabled={formData.autoRegistrationEnabled || formData.autoSyncEnabled}
                            dataTestId='zoom-registration-toggle'
                        />
                        <Text
                            className={'m-left-8px'}
                            message={formData.registrationEnabled ? 'Registration enabled' : 'Registration disabled'}
                            id={formData.registrationEnabled ? 'oe.zoomWebinar.registrationEnabled' : 'oe.zoomWebinar.registrationDisabled'}
                        />
                    </FormField>
                    {
                        formData.registrationEnabled &&
                        <FormField className='form-toggle'>
                            <OEToggle
                                isSelected={formData.approvalType === ApprovalType.AUTOMATICALLY}
                                onClick={formData.toggleApprovalType}
                                isDisabled={formData.autoRegistrationEnabled || formData.autoSyncEnabled}
                                dataTestId='zoom-autoApproval-toggle'
                            />
                            <Text
                                className={'m-left-8px'}
                                message={formData.approvalType === ApprovalType.AUTOMATICALLY ? 'Automatic approval' : 'Manual approval'}
                                id={formData.approvalType === ApprovalType.AUTOMATICALLY ? 'oe.zoomWebinar.automaticApproval' : 'oe.zoomWebinar.manualApproval'}
                            />
                        </FormField>
                    }
                    {
                        formData.registrationEnabled &&
                        hasPermission(Permission.CREATE_ZOOM_CONNECTIONS) && (
                            <FormField className='form-toggle'>
                                <OEToggle
                                    onClick={formData.toggleEmailConfirmation}
                                    isSelected={formData.registrantsConfirmationEmail}
                                    isDisabled={formData.autoRegistrationEnabled || formData.autoSyncEnabled}
                                    dataTestId='zoom-registrationEmail-toggle'
                                />
                                <Text
                                    className={'m-left-8px'}
                                    message={formData.registrantsConfirmationEmail ? 'Zoom registration emails enabled' : 'Zoom registration emails disabled'}
                                    id={formData.registrantsConfirmationEmail ? 'oe.zoomWebinar.emailNotificationEnabled' : 'oe.zoomWebinar.emailNotificationDisabled'}
                                />
                            </FormField>
                        )
                    }
                </>
            }
        </>);
});

export { ScheduledMeetingForm };
